/**
 * Loads all storefront bootstrap data before the app is shown.
 */
import { useEffect, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import i18n from '@/lib/i18n'
import { HOME_PAGE_SLUG } from '@shared/constants/cms'
import { useAuthStore } from '@/stores/authStore'
import {
  fetchBootstrapAuth,
  fetchBootstrapCart,
  fetchBootstrapCurrency,
  fetchBootstrapFeatures,
  fetchBootstrapFooter,
  fetchBootstrapHeader,
  fetchBootstrapHomeBanners,
  fetchBootstrapHomePage,
  fetchBootstrapMenu,
  fetchBootstrapTheme,
} from '@/lib/bootstrapQueries'

const DEFAULT_MENU_SLUG = 'main-menu'

function useI18nBootstrapReady() {
  const [ready, setReady] = useState(i18n.isInitialized)

  useEffect(() => {
    const load = () => {
      i18n.loadNamespaces(['common'], () => setReady(true))
    }

    if (i18n.isInitialized) {
      load()
      return
    }

    i18n.on('initialized', load)
    return () => {
      i18n.off('initialized', load)
    }
  }, [])

  return ready
}

export function useAppBootstrap() {
  const setUser = useAuthStore((s) => s.setUser)
  const setAuthLoading = useAuthStore((s) => s.setLoading)
  const i18nReady = useI18nBootstrapReady()

  const headerQuery = useQuery({
    queryKey: ['cms-header'],
    queryFn: fetchBootstrapHeader,
    staleTime: 30 * 1000,
    retry: 1,
  })

  const menuSlug = headerQuery.data?.menu_slug ?? DEFAULT_MENU_SLUG

  const parallelQueries = useQueries({
    queries: [
      {
        queryKey: ['storefront', 'features'],
        queryFn: fetchBootstrapFeatures,
        staleTime: 30 * 1000,
        retry: 1,
      },
      {
        queryKey: ['cms-theme'],
        queryFn: fetchBootstrapTheme,
        staleTime: 1000 * 60 * 10,
        retry: 1,
      },
      {
        queryKey: ['auth', 'me'],
        queryFn: fetchBootstrapAuth,
        retry: false,
        staleTime: 1000 * 60 * 5,
      },
      {
        queryKey: ['cms-footer'],
        queryFn: fetchBootstrapFooter,
        staleTime: 30 * 1000,
        retry: 1,
      },
      {
        queryKey: ['config', 'currency'],
        queryFn: fetchBootstrapCurrency,
        staleTime: 1000 * 60 * 5,
        retry: 1,
      },
      {
        queryKey: ['cart'],
        queryFn: fetchBootstrapCart,
        staleTime: 0,
        retry: 1,
      },
      {
        queryKey: ['public-menu', menuSlug],
        queryFn: () => fetchBootstrapMenu(menuSlug),
        enabled: headerQuery.isFetched,
        staleTime: 60 * 1000,
        retry: false,
      },
    ],
  })

  const [features, theme, auth, footer, currency, cart, menu] = parallelQueries

  const homeBannersQuery = useQuery({
    queryKey: ['storefront', 'home-banners'],
    queryFn: fetchBootstrapHomeBanners,
    enabled: features.isFetched,
    staleTime: 60 * 1000,
    retry: 1,
  })

  const homePageQuery = useQuery({
    queryKey: ['static-page', HOME_PAGE_SLUG],
    queryFn: fetchBootstrapHomePage,
    enabled: features.isFetched && !!features.data?.enable_static_pages,
    staleTime: 0,
    retry: false,
  })

  useEffect(() => {
    if (!auth.isFetched) return
    setUser(auth.data ?? null)
    setAuthLoading(false)
  }, [auth.isFetched, auth.data, setUser, setAuthLoading])

  const needsHomeBanners = !!features.data?.enable_home_banner_slider
  const needsHomeCms = !!features.data?.enable_static_pages

  const coreReady =
    features.isFetched &&
    theme.isFetched &&
    auth.isFetched &&
    headerQuery.isFetched &&
    footer.isFetched &&
    currency.isFetched &&
    cart.isFetched &&
    (headerQuery.isFetched ? menu.isFetched : false) &&
    (!needsHomeBanners || homeBannersQuery.isFetched) &&
    (!needsHomeCms || homePageQuery.isFetched)

  const isReady = coreReady && i18nReady

  const isLoading = !isReady

  return { isReady, isLoading }
}
